<template>
  <modal-component
    :is-displayed="isEditModalOpened"
    :no-padding="true"
    :title="$t('Authorization.EditTitle')"
    @toggle-modal="$emit('toggle-modal')"
  >
    <form-wrapper :is-form-in-modal="true" :is-loading="isLoading">
      <form @submit.prevent="handleSubmit">
        <form-divider>
          <div class="relative items-center py-4">
            <label class="whitespace-nowrap text-sm font-medium not-italic text-gray-800">{{
              $t("Authorization.User")
            }}</label>
            <div class="flex items-center justify-center">
              <input-field v-model="selectedResource.createdUserEmail" disabled="true" :no-padding="true" />
            </div>
          </div>
          <div class="relative items-center py-4">
            <label class="whitespace-nowrap text-sm font-medium not-italic text-gray-800">{{
              $t("Authorization.ResourceType")
            }}</label>
            <div class="flex items-center justify-center">
              <input-field v-model="selectedResource.resourceTypeName" disabled="true" :no-padding="true" />
            </div>
          </div>
          <div v-if="resource.resourceTypeId === 2" class="relative items-center py-4">
            <label class="whitespace-nowrap text-sm font-medium not-italic text-gray-800">{{
              $t("Authorization.Asset")
            }}</label>
            <div class="flex items-center justify-center">
              <v-select
                v-model="resource.onResourceId"
                :class="`mt-1 w-full`"
                :get-option-label="(device: any) => device?.assignment?.name"
                :options="deviceAssignment"
                :reduce="(device : any) => device.assignmentId"
                :clearable="false"
              ></v-select>
            </div>
          </div>
          <div class="relative items-center py-4">
            <label class="whitespace-nowrap text-sm font-medium not-italic text-gray-800">{{
              $t("Authorization.Role")
            }}</label>
            <div class="flex items-center justify-center">
              <v-select
                v-model="resource.roleId"
                :class="`mt-1 w-full`"
                :options="roles"
                :reduce="(device : any) => device.id"
                label="name"
                :clearable="false"
              ></v-select>
            </div>
          </div>
        </form-divider>
        <button-wrapper :is-submit-loading="isSubmitLoading" />
      </form>
    </form-wrapper>
  </modal-component>
</template>
<script lang="ts">
import { defineComponent } from "vue";
import ModalComponent from "@/core/Components/Modal.vue";
import FormWrapper from "@/core/Components/FormElements/FormWrapper.vue";
import FormDivider from "@/core/Components/FormElements/FormDivider.vue";
import ButtonWrapper from "@/core/Components/FormElements/ButtonWrapper.vue";
import AuthorizationService from "@/modules/AdminDashboard/Submodules/Authorization/Services/AuthorizationService";
import { useToast } from "vue-toastification";
import { CheckObjectsForPatch, ErrorMessageFormatter } from "@/core/Services/GlobalFunctions";
import VSelect from "vue-select";
import RoleService from "@/modules/AdminDashboard/Submodules/Authorization/Services/RoleService";
import UserService, { User } from "@/modules/Users/Services/UserService";
import AssignmentService, {
  DeviceAssignment,
} from "@/modules/Devices/Submodules/Assignments/Services/AssignmentService";
import { Role } from "@/modules/AdminDashboard/Submodules/Authorization/Services/Types/RoleTypes";
import { ResourceType } from "@/modules/AdminDashboard/Submodules/Authorization/Services/Types/AuthorizationTypeType";
import { useServiceStore } from "@/core/Store/serviceStore";
import InputField from "@/core/Components/FormElements/InputField.vue";

export default defineComponent({
  components: {
    VSelect,
    ButtonWrapper,
    FormDivider,
    FormWrapper,
    ModalComponent,
    InputField,
  },
  props: {
    isEditModalOpened: Boolean,
    selectedResource: {
      type: Object,
      required: true,
    },
    refreshData: {
      type: Function,
      required: true,
    },
  },
  emits: ["toggle-modal"],
  data() {
    return {
      authorizationService: new AuthorizationService(),
      roleService: new RoleService(),
      userService: new UserService(),
      deviceAssignmentService: new AssignmentService(),
      roles: [] as Role[],
      users: [] as User[],
      deviceAssignment: [] as DeviceAssignment[],
      resourceTypes: [] as ResourceType[],
      toast: useToast(),
      serviceStore: useServiceStore(),
      isLoading: false,
      isSubmitLoading: false,
      resourceFromApi: {},
      resource: {
        toCompanyId: null,
        toUserId: null,
        onCompanyId: null,
        resourceTypeId: null,
        onResourceId: null,
        roleId: null,
        permissionIds: [],
        createdUserId: null,
        policyId: null,
        companyId: null,
      } as any,
    };
  },
  async created() {
    this.isLoading = true;
    try {
      await Promise.all([
        this.getRoles(),
        this.getResource(),
        //this.getDeviceAssignments(),
      ]);
    } catch (error) {
      this.toast.error(ErrorMessageFormatter(error));
    } finally {
      this.isLoading = false;
    }
  },
  methods: {
    async handleSubmit() {
      this.isSubmitLoading = true;
      try {
        const patchObj = await CheckObjectsForPatch({
          initialObject: this.resourceFromApi,
          editedObject: this.resource,
        });

        await this.authorizationService.editResource(this.selectedResource.id, patchObj);
        this.toast.success((this as any).$t("General.EditedSuccessfully"));
        this.$emit("toggle-modal");
        await this.refreshData();
      } catch (error) {
        this.toast.error(ErrorMessageFormatter(error));
      } finally {
        this.isSubmitLoading = false;
      }
    },
    async getResource() {
      try {
        const response = await this.authorizationService.getResourceById(this.selectedResource.id);
        Object.assign(this.resourceFromApi, response);
        this.resource = response;
      } catch (error) {
        this.toast.error(ErrorMessageFormatter(error));
      }
    },
    async getRoles() {
      try {
        this.roles = await this.roleService.getAllRoles({
          pageNumber: 1,
          pageSize: 100000,
          allRoles: true,
        });
      } catch (error) {
        this.toast.error(ErrorMessageFormatter(error));
      }
    },
    async getDeviceAssignments() {
      try {
        this.deviceAssignment = await this.deviceAssignmentService.getAllDeviceAssignments({
          pageNumber: 1,
          pageSize: 100000,
          typeOfAssignment: "Company",
        });
      } catch (error) {
        this.toast.error(ErrorMessageFormatter(error));
      }
    },
  },
});
</script>
<style scoped></style>
